/**
 * CustomerTicketAlarm — emergency siren for new customer-created tickets
 * ---------------------------------------------------------------------
 * Sits next to `TicketBell` in the topbar. Polls
 * `/api/tickets/customer-created/poll` every 10 s and — when a NEW
 * customer-created ticket appears (grievance from /me, chat handoff,
 * WhatsApp, email) — synthesizes a two-tone siren via the Web Audio
 * API. Web Audio synthesis is used instead of an mp3 asset so the
 * alarm works even if the asset host is unreachable and can be tuned
 * live without redeploying media.
 *
 * The mute state is shared with `TicketBell` via `localStorage`
 * (`rescigo.ticket_bell.muted`) so one toggle silences both.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SirenIcon } from "./SirenIcon";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_MS = 10000;
const MUTE_KEY = "rescigo.ticket_bell.muted";

function getToken() {
  return localStorage.getItem("rescigo_token") || "";
}

// -------- Web Audio siren synthesis --------
// Two-tone urgent siren: A5 (880 Hz) alternated with C5 (523 Hz).
// Runs for ~1.8 s so it's noticeable but not annoying. Volume gain
// is capped at 0.4 to stay well below the OS master volume.
function playSiren() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0, ctx.currentTime);
    gain.connect(ctx.destination);
    const osc = ctx.createOscillator();
    osc.type = "square";
    osc.connect(gain);
    // Alternate between the two tones every 150 ms for 1.8 s
    const pattern = [880, 523, 880, 523, 880, 523, 880, 523, 880, 523, 880, 523];
    pattern.forEach((freq, i) => {
      const t = ctx.currentTime + i * 0.15;
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0.35, t);
      gain.gain.setValueAtTime(0.0, t + 0.12); // brief silence between tones
    });
    // Small ramp-out to avoid a click at the tail
    gain.gain.linearRampToValueAtTime(0.0, ctx.currentTime + 1.85);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 1.9);
    osc.onended = () => { try { ctx.close(); } catch { /* noop */ } };
  } catch { /* AudioContext unavailable — silently skip */ }
}

export default function CustomerTicketAlarm() {
  const [count, setCount] = useState(0);
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === "1");
  const lastTicketNoRef = useRef(null);
  const firstTickRef = useRef(true);
  const navigate = useNavigate();

  // Keep the mute state in sync with the sibling TicketBell — when
  // that widget toggles the localStorage key, we hear it too.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === MUTE_KEY) setMuted(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API}/tickets/customer-created/poll`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        setCount(d.open_customer_created || 0);
        const isFirst = firstTickRef.current;
        firstTickRef.current = false;
        const newestTn = d.latest?.ticket_no || null;
        const changed  = newestTn && newestTn !== lastTicketNoRef.current;
        lastTicketNoRef.current = newestTn;
        if (!isFirst && changed && !muted && d.latest) {
          playSiren();
          const t = d.latest;
          toast.error(
            `🚨 New customer ticket · ${t.ticket_no}`,
            {
              description: `${t.customer_name || "Customer"} · ${
                (t.issue || "").slice(0, 90) || "Tap to review"
              }`,
              action: {
                label: "Open",
                onClick: () => navigate("/crm/tickets?tab=all"),
              },
              duration: 8000,
            }
          );
          // Also fire native browser Notification if the topbar bell has
          // permission (shared key with TicketBell).
          try {
            if (typeof window !== "undefined" &&
                "Notification" in window &&
                Notification.permission === "granted" &&
                document.hidden) {
              const n = new Notification(
                `🚨 New customer ticket · ${t.ticket_no}`,
                {
                  body: `${t.customer_name || "Customer"} · ${
                    (t.issue || "").slice(0, 120)}`,
                  icon: "/favicon.ico",
                  tag: `customer-ticket-${t.ticket_no}`,
                  requireInteraction: true,
                }
              );
              n.onclick = () => {
                window.focus();
                navigate("/crm/tickets?tab=all");
                n.close();
              };
            }
          } catch { /* Notification constructor unsupported */ }
        }
      } catch { /* offline / auth race — swallow */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [muted, navigate]);

  const testAlarm = () => playSiren();

  return (
    <div className="flex items-center gap-0.5">
      <button
        onClick={() => navigate("/crm/tickets?tab=all")}
        data-testid="topbar-customer-alarm"
        title={
          count > 0
            ? `${count} open customer ticket(s) — click to review`
            : "Emergency alarm — rings on new customer tickets"
        }
        className={`relative p-2 rounded-md transition-colors ${
          count > 0
            ? "text-[#7A2E18] hover:bg-[#F4C7B9]/40"
            : "text-[#8A8780] hover:text-[#1A1A1A] hover:bg-[#F0EFEA]"
        }`}
      >
        <SirenIcon size={18} weight={count > 0 ? "fill" : "regular"} />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 bg-[#7A2E18] text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1"
            data-testid="topbar-customer-alarm-count"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      <button
        onClick={testAlarm}
        data-testid="topbar-customer-alarm-test"
        title="Play a test siren"
        className="p-1 text-[9px] uppercase tracking-[0.12em] font-semibold text-[#8A8780] hover:text-[#7A2E18]"
      >
        Test
      </button>
    </div>
  );
}
